import React from "react";
import classNames from "classnames";

interface Props extends React.HTMLProps<HTMLDivElement> {
  bg?: string;
  containerClassName?: string;
  color?: string;
  left?: boolean;
  right?: boolean;
}

/**
 * Bleeds the background color over the whole width of the page.
 *
 * This component works by absolutely positioning two divs to the side of the children. To prevent the page from
 * scrolling horizontally we have to add the following to the first element in the body
 *
 * > h-screen min-h-screen w-screen overflow-x-hidden
 *
 * Without the height classes sticky position elements in the page would not work. If this was place on the body element
 * the overflow hidden would be ignored on mobile.
 *
 * On mobile the bleeder will always bleed to both sides.
 *
 * There is another problem on very large screens (+2000px wide) that the left bleed does not seem to get bigger than
 * the relative parent. This is solved by putting a second bleed this time placed at 200% instead of 100%.
 */
export const Bleeder = ({
  children,
  className,
  containerClassName,
  bg,
  color,
  left = true,
  right = true,
}: React.PropsWithChildren<Props>) => {
  if (!color && !bg) {
    return (
      <div className={classNames("relative", containerClassName)}>
        <div className={className}>{children}</div>
      </div>
    );
  }

  return (
    <div
      className={classNames("relative", containerClassName, bg)}
      style={{ backgroundColor: color }}
    >
      <div
        className={classNames(
          "absolute bottom-0 right-[100%] top-0 w-screen md:hidden",
          bg
        )}
        style={{ backgroundColor: color }}
      />
      {left && (
        <>
          <div
            className={classNames(
              "absolute bottom-0 right-[100%] top-0 hidden w-screen md:block",
              bg
            )}
            style={{ backgroundColor: color }}
          />
          <div
            className={classNames(
              "absolute bottom-0 right-[200%] top-0 hidden w-screen xl:block",
              bg
            )}
            style={{ backgroundColor: color }}
          />
        </>
      )}
      <div
        className={classNames(
          "min-w-screen absolute bottom-0 left-[100%] top-0 w-screen md:hidden",
          bg
        )}
        style={{ backgroundColor: color }}
      />
      {right && (
        <>
          <div
            className={classNames(
              "absolute bottom-0 left-[100%] top-0 hidden w-screen md:block",
              bg
            )}
            style={{ backgroundColor: color }}
          />
          <div
            className={classNames(
              "absolute bottom-0 left-[200%] top-0 hidden w-screen xl:block",
              bg
            )}
            style={{ backgroundColor: color }}
          />
        </>
      )}
      <div className={className}>{children}</div>
    </div>
  );
};

interface Props extends React.HTMLProps<HTMLDivElement> {
  bg?: string;
  left?: boolean;
  right?: boolean;
}

export const BleedLeft = ({
  children,
  ...props
}: React.PropsWithChildren<Props>) => {
  return (
    <Bleeder {...props} right={false}>
      {children}
    </Bleeder>
  );
};

export const BleedRight = ({
  children,
  ...props
}: React.PropsWithChildren<Props>) => {
  return (
    <Bleeder {...props} left={false}>
      {children}
    </Bleeder>
  );
};
